import { dispatch } from 'nike-re-framejs';
import { dispatchUploadProgress, setVersion } from './uploadProgress';
import axios from 'axios';
import i18n from '../../common/i18n';

const NON_IMAGE_FILE_SIZE_LIMIT = 5 * 1024 * 1024;

class ImageUtils {
    static _readFileAsUrl(uploadContext) {
        const file = uploadContext.get('file');
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onload = event => {
                resolve(uploadContext.setIn(['image', 'url'], event.target.result));
            };
            reader.onerror = error => reject(error);
            reader.onabort = error => reject(error);
            reader.readAsDataURL(file);
        });
    }

    static _getImage(uploadContext) {
        const url = uploadContext.getIn(['image', 'url']);
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
                resolve(uploadContext.setIn(['image', 'imageObject'], image));
            };
            image.onerror = e => reject(e);
            image.src = url;
        });
    }

    static _resizeImage(uploadContext) {
        const file = uploadContext.get('file');
        const image = uploadContext.getIn(['image', 'imageObject']);

        const maxSizeMB = 1;
        const maxWidth = 1024;
        const maxHeight = 768;
        const quality = 0.7;

        const canvasToBlob = function(canvas, type) {
            let byteString = atob(canvas.split(',')[1]),
                ab = new ArrayBuffer(byteString.length),
                ia = new Uint8Array(ab),
                i;
            for (i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }
            return new Blob([ab], {
                type: type
            });
        };
        return new Promise((resolve, reject) => {
            let fileType = file.type;
            let fileSize = file.size;
            let width = image.width;
            let height = image.height;
            const maxSizeB = parseInt(maxSizeMB, 10) > 0 ? maxSizeMB * 1000000 : 0;
            if (maxSizeB < fileSize) {
                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }
            }
            let canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(image, 0, 0, width, height);
            const resizedData = {};
            resizedData.url = canvas.toDataURL(fileType, quality);
            resizedData.blob = canvasToBlob(resizedData.url, fileType);
            resizedData.type = fileType;
            resolve(
                uploadContext
                    .setIn(['image', 'resized'], resizedData)
                    .removeIn(['image', 'imageObject'])
                    .removeIn(['image', 'url'])
            );
        });
    }
}

export class UploadUtils {
    static upload(uploadContext) {
        dispatchUploadProgress(uploadContext, 1);

        const formData = new FormData();
        const resized = uploadContext.getIn(['image', 'resized']);
        if (resized) {
            formData.append('file', resized.blob, uploadContext.get('filename'), { type: resized.type });
        } else {
            const file = uploadContext.get('file');
            formData.append('file', file, uploadContext.get('filename'));
        }

        const { idSlot, idDocDms, page, clientHash } = uploadContext.get('slotDescription');
        formData.append('idSlot', idSlot);
        formData.append('idDocDms', idDocDms);
        formData.append('page', page);
        formData.append('clientHash', clientHash);

        return axios
            .request({
                url: '/api/dms/save',
                data: formData,
                method: 'POST',
                onUploadProgress: function(progressEvent) {
                    console.log('progressEvent', progressEvent);
                    const progress = (progressEvent.loaded / progressEvent.total) * 100;
                    dispatchUploadProgress(uploadContext, progress, 100);
                },
                withCredentials: true
            })
            .then(response => {
                dispatchUploadProgress(uploadContext, 101, 500);
                return uploadContext.set('response', response);
            });
    }

    static determineFileType(uploadContext) {
        const file = uploadContext.get('file');
        return new Promise((resolve, reject) => {
            let resolved = null;
            if (/(jpe?g|png|gif)$/i.test(file.type)) {
                resolved = 'image';
            }
            if (/(pdf)$/i.test(file.type)) {
                resolved = 'pdf';
            }
            if (!resolved) {
                reject({ uploadErrorText: i18n('nui.documentUpload.unsuportedType') });
            }
            resolve(uploadContext.set('resolvedType', resolved).set('filename', Date.now() + '_' + file.name));
        });
    }

    static resizeIfImage(uploadContext) {
        if (uploadContext.get('resolvedType') === 'image') {
            return Promise.resolve(uploadContext)
                .then(ImageUtils._readFileAsUrl)
                .then(ImageUtils._getImage)
                .then(ImageUtils._resizeImage);
        }
        return uploadContext;
    }

    static validateNonImageFileSize(uploadContext) {
        if (uploadContext.get('resolvedType') !== 'image') {
            const file = uploadContext.get('file');
            if (file.size > NON_IMAGE_FILE_SIZE_LIMIT) {
                return Promise.reject({
                    uploadErrorText: `${i18n('nui.documentUpload.fileSizeLimit')} ${bytes(NON_IMAGE_FILE_SIZE_LIMIT)}.`
                });
            }
        }
        return uploadContext;
    }
    static persistVersion(uploadContext) {
        const response = uploadContext.get('response');
        // check also for missing version in responses which result from 30x redirects
        if (!response?.version) {
            return Promise.reject('no version in uploadVersion');
        }
        dispatch(setVersion(uploadContext, response.version));
        return uploadContext;
    }
}

function bytes(value) {
    let ret = '';
    if (typeof value === 'number') {
        const kB = 1000;
        const MB = 1000 * kB;
        const GB = 1000 * MB;
        let unit = 'B';
        let v = value;
        if (value >= GB) {
            v = value / GB;
            unit = 'GB';
        } else if (value >= MB) {
            v = value / MB;
            unit = 'MB';
        } else if (value >= 1000) {
            v = value / kB;
            unit = 'kB';
        }
        ret = v.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1\u00a0');
        if (ret.endsWith('.00')) {
            ret = ret.substring(0, ret.length - 3);
        } else {
            ret = ret.replace('.', ',');
        }
        ret = `${ret}\u00a0${unit}`;
    }
    return ret;
}
